import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Header, Grid, Popup, Icon, Divider } from 'semantic-ui-react';
import { Line } from 'react-chartjs-2';
import { CacheInputs } from 'actions';

import { graph, colors } from 'utils/colors';
import { translation as translationPlanning } from '../../Translation/planning';

const SAVING_OPTIONS = [
  {
    key: '0.5',
    value: 0.5,
    label: '50%',
    accent: '#b8c4ff',
    border: '#8e9df0',
  },
  {
    key: '0.75',
    value: 0.75,
    label: '75%',
    accent: '#6b8cff',
    border: '#6b7be8',
  },
  {
    key: '0.9',
    value: 0.9,
    label: '90%',
    accent: '#1f2e86',
    border: '#1f2e86',
  },
];

const GAUGE_LENGTH = Math.PI * 48;

function Gauge({ value, label, accent }) {
  return (
    <GaugeWrapper>
      <svg viewBox="0 0 120 60" width="100%" role="img" aria-label={label}>
        <path
          d="M 12 60 A 48 48 0 0 1 108 60"
          fill="none"
          stroke="#eeeeee"
          strokeWidth="14"
        />
        <path
          d="M 12 60 A 48 48 0 0 1 108 60"
          fill="none"
          stroke={accent}
          strokeWidth="14"
          strokeDasharray={`${value * GAUGE_LENGTH} ${GAUGE_LENGTH}`}
        />
        <text
          x="60"
          y="60"
          textAnchor="middle"
          fontSize="19"
          fontWeight="700"
          fill="#1f2e86"
        >
          {label}
        </text>
      </svg>
    </GaugeWrapper>
  );
}

Gauge.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

class SavingResult extends Component {
  state = {
    monthlySavingChosen: this.props.targetSavingInputs.monthlySavingChosen,
    paths:
      this.props.targetSavingInputs.monthlySavingChosen && this.props.result
        ? this.props.result.paths[
            this.props.targetSavingInputs.monthlySavingChosen
          ]
        : null,
    maxOfChart: null,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.result !== this.props.result) {
      this.setState({
        maxOfChart: null,
        monthlySavingChosen: null,
      });
    }
  }

  getChartData = () => {
    const { paths } = this.state;
    const { french } = this.props;
    const language = french ? 'fr' : 'en';

    if (paths) {
      const { current, high, low, min } = paths;
      const dataLength = Object.values(current).length;

      return {
        labels: new Array(dataLength)
          .fill(0)
          .map((n, index) => dataLength - index - 1),
        datasets: [
          {
            data: Object.values(min),
            label: '',
            lineTension: 0,
            fill: false,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            pointBackgroundColor: 'transparent',
            pointBorderColor: 'transparent',
          },
          {
            data: Object.values(low),
            borderWidth: 3,
            label: translationPlanning[language].TARGET_SAVING.GRAPH_LEGEND_LOW,
            lineTension: 0,
            fill: 0,
            backgroundColor: graph.teal,
            borderColor: graph.teal,
            pointRadius: 0,
            pointBackgroundColor: graph.teal,
            pointBorderColor: graph.teal,
            pointHitRadius: 5,
          },
          {
            data: Object.values(current),
            borderWidth: 3,
            label:
              translationPlanning[language].TARGET_SAVING.GRAPH_LEGEND_MIDDLE,
            lineTension: 0,
            fill: 0,
            backgroundColor: graph.darkBlue,
            borderColor: graph.darkBlue,
            pointRadius: Array(dataLength - 1)
              .fill(0)
              .concat([5]),
            pointBackgroundColor: Array(dataLength - 1)
              .fill(graph.darkBlue)
              .concat([colors.red]),
            pointBorderColor: Array(dataLength - 1)
              .fill(graph.darkBlue)
              .concat([colors.red]),
            pointHitRadius: 5,
          },
          {
            data: Object.values(high),
            borderWidth: 3,
            label:
              translationPlanning[language].TARGET_SAVING.GRAPH_LEGEND_HIGH,
            lineTension: 0,
            fill: 0,
            backgroundColor: graph.lightBlue,
            borderColor: graph.lightBlue,
            pointRadius: 0,
            pointBackgroundColor: graph.lightBlue,
            pointBorderColor: graph.lightBlue,
            pointHitRadius: 5,
          },
        ],
      };
    }

    return {};
  };

  setChartMax = (label, index, values) => {
    const { maxOfChart } = this.state;

    if (values[0] > maxOfChart) {
      this.setState({ maxOfChart: values[0] });
    }
  };

  getOutcomeData = () => {
    const { result } = this.props;
    const { monthlySavingChosen } = this.state;

    if (!result || monthlySavingChosen === null) return null;
    if (!result.investment_outcomes) return null;

    const key = String(monthlySavingChosen);
    return result.investment_outcomes[key] || null;
  };

  isAble = (input) => {
    const { result } = this.props;

    if (!result) return false;

    return !!result.savings[input];
  };

  handleCardClick = (option) => {
    const { result } = this.props;
    const { monthlySavingChosen } = this.state;

    if (!this.isAble(option.key)) return;

    if (monthlySavingChosen === option.value) {
      this.updateMonthlySavingChosen(null, null);
    } else {
      this.updateMonthlySavingChosen(result.savings[option.key], option.value);
    }
  };

  updateMonthlySavingChosen = (value, option) => {
    const { result, dispatch } = this.props;

    this.setState({
      monthlySavingChosen: option,
      paths: result.paths[option],
    });
    dispatch(
      CacheInputs.updateField('planning', 'target_saving', {
        monthlySavingChosen: option,
      })
    );

    this.scrollToBottom();
  };

  scrollHandler = (to, duration) => {
    if (duration <= 0) return false;
    const el = document.scrollingElement || document.documentElement;
    const difference = to - el.scrollTop;
    const perTick = (difference / duration) * 10;

    return setTimeout(() => {
      el.scrollTop += perTick;
      if (el.scrollTop === to) {
        return false;
      }
      return this.scrollHandler(to, duration - 10);
    }, 10);
  };

  scrollToBottom = () => this.scrollHandler(document.body.scrollHeight, 500);

  render() {
    const { result, inf, french, startingAmount, yearsToTarget } = this.props;
    const { monthlySavingChosen, maxOfChart } = this.state;

    const outcomeData = this.getOutcomeData();

    let monthlySavingAmount = null;
    if (monthlySavingChosen !== null && result && result.savings) {
      monthlySavingAmount = result.savings[String(monthlySavingChosen)] || null;
    }

    const language = french ? 'fr' : 'en';
    const chartOptions = {
      hover: {
        animationDuration: 0,
        onHover: (e) => {
          e.target.style.cursor = 'pointer';
        },
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: true,
              drawTicks: true,
              drawOnChartArea: false,
            },
            scaleLabel: {
              display: true,
              labelString:
                translationPlanning[language].TARGET_SAVING.GRAPH_X_AXIS,
              fontStyle: 'bold',
              fontSize: 16,
            },
            ticks: {
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              callback(value, index, values) {
                const totalTicks = values.length;
                const step = Math.ceil(totalTicks / 20);
                return index % step === 0 || index === totalTicks - 1
                  ? value
                  : '';
              },
              fontSize: 14,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              callback: (label) => `$${label.toLocaleString()}`,
              max: maxOfChart,
              fontSize: 14,
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem, data) => `${data.labels[tooltipItem[0].index]}`,
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];

            if (tooltipItem.datasetIndex === 0) return null;
            const labelWords = dataset.label.split(' ');
            const lastTwoWords = labelWords.slice(-2).join(' ');
            return `${lastTwoWords}: $${Math.ceil(
              Number(tooltipItem.yLabel)
            ).toLocaleString()}`;
          },
          labelColor: (tooltipItem, chart) => {
            const dataset =
              chart.config.data.datasets[tooltipItem.datasetIndex];
            return {
              borderColor: dataset.borderColor,
              backgroundColor: dataset.backgroundColor,
            };
          },
        },
        mode: 'index',
        bodySpacing: 5,
      },
      legend: {
        position: 'top',
        labels: {
          filter(legendItem) {
            return legendItem.text !== '';
          },
        },
      },
    };

    return !result ? null : (
      <div style={{ padding: '0 3rem 2rem' }}>
        <Divider />
        <div style={{ maxWidth: '1200px', margin: 'auto' }}>
          <div
            style={{
              visibility: 'hidden',
              height: '2rem',
            }}
          >
            <Line
              setChartMax={this.setChartMax}
              data={() => {
                const dataSets = [];
                const values = Object.values(result.paths);

                values.forEach((v) => {
                  if (v) dataSets.push(...Object.values(v));
                });
                const temp = dataSets.map((d, index) => ({
                  data: Object.values(d),
                  borderWidth: 2,
                  label: index,
                  lineTension: 0,
                  fill: false,
                  backgroundColor: graph.red,
                  borderColor: graph.red,
                  pointBackgroundColor: graph.red,
                  pointBorderColor: graph.red,
                  pointRadius: 0,
                  pointHitRadius: 5,
                }));

                return {
                  datasets: temp,
                };
              }}
              options={{
                scales: {
                  xAxes: [
                    {
                      gridLines: {
                        display: false,
                      },
                    },
                  ],
                  yAxes: [
                    {
                      ticks: {
                        callback: (label, index, values) =>
                          this.setChartMax(label, index, values),
                      },
                    },
                  ],
                },
                tooltips: {
                  callbacks: {
                    title: (tooltipItem, data) =>
                      `${data.labels[tooltipItem[0].index]}`,
                    label: (tooltipItems, data) =>
                      `${
                        data.datasets[tooltipItems.datasetIndex].label
                      }: $${Math.ceil(
                        Number(tooltipItems.yLabel)
                      ).toLocaleString()}`,
                  },
                },
              }}
            />
          </div>
          <Header textAlign="left" size="large" style={{ maxWidth: '55rem' }}>
            {translationPlanning[language].TARGET_SAVING.RESULTS_TITLE}
            <Header.Subheader style={{ margin: '.3rem auto .6rem' }}>
              {translationPlanning[language].TARGET_SAVING.RESULTS_SUBTITLE}
            </Header.Subheader>
          </Header>
          <Grid style={{ margin: '1rem 0' }}>
            <Grid.Row>
              <Grid.Column>
                <Header
                  textAlign="center"
                  size="small"
                  style={{ maxWidth: '55rem', fontSize: '16px' }}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {
                      translationPlanning[language].TARGET_SAVING
                        .LIKELIHOOD_OF_SUCCESS
                    }{' '}
                    <Popup
                      trigger={
                        <Icon
                          name="question circle outline"
                          style={{
                            color: 'dimgrey',
                            marginLeft: '3px',
                            fontSize: '1em',
                            marginBottom: '5px',
                          }}
                        />
                      }
                      position="right center"
                      content={
                        translationPlanning[language].TARGET_SAVING
                          .LIKELIHOOD_POPUP
                      }
                    />
                  </span>
                  <Header.Subheader style={{ margin: '.3rem auto .6rem' }}>
                    {
                      translationPlanning[language].TARGET_SAVING
                        .LIKELIHOOD_TEXT_1
                    }{' '}
                    <span
                      style={{
                        margin: '0 0.2rem',
                        color: colors.navyBlue,
                        fontWeight: 'bold',
                        fontSize: '1.3rem',
                      }}
                    >
                      {`${Math.trunc(result.prob_without_savings * 100)} %`}
                    </span>
                    .<br />
                    {
                      translationPlanning[language].TARGET_SAVING
                        .LIKELIHOOD_TEXT_2
                    }
                  </Header.Subheader>
                </Header>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={3} style={{ padding: 0 }}>
              {SAVING_OPTIONS.map((option) => {
                const able = this.isAble(option.key);
                const selected = monthlySavingChosen === option.value;

                return (
                  <Grid.Column key={option.key}>
                    <OptionCard
                      selected={selected}
                      able={able}
                      borderColor={option.border}
                      onClick={() => this.handleCardClick(option)}
                    >
                      <Gauge
                        value={option.value}
                        label={option.label}
                        accent={option.accent}
                      />
                      <CardDivider />
                      <CardLabel>
                        {
                          translationPlanning[language].TARGET_SAVING
                            .REQUIRED_MONTHLY_SAVINGS
                        }
                      </CardLabel>
                      <CardValue>
                        {french
                          ? `${result.savings[option.key]} $`
                          : `$${result.savings[option.key]}`}
                        <CardValueUnit>
                          {
                            translationPlanning[language].TARGET_SAVING
                              .MONTHLY_ABBREVIATION
                          }
                        </CardValueUnit>
                      </CardValue>
                      {able ? (
                        <DetailsButton
                          type="button"
                          selected={selected}
                          borderColor={option.border}
                        >
                          <Icon name="chart line" />
                          {selected
                            ? translationPlanning[language].TARGET_SAVING
                                .HIDE_DETAILS
                            : translationPlanning[language].TARGET_SAVING
                                .SHOW_DETAILS}
                        </DetailsButton>
                      ) : null}
                    </OptionCard>
                  </Grid.Column>
                );
              })}
            </Grid.Row>
          </Grid>

          {monthlySavingChosen !== null ? (
            <ResultsContainer>
              <ResultsTitle>
                {
                  translationPlanning[language].TARGET_SAVING
                    .RESULTS_SAVING_PREFIX
                }{' '}
                <ResultsAccent color={colors.blue}>
                  {monthlySavingAmount !== null
                    ? french
                      ? `${monthlySavingAmount.toLocaleString()} $`
                      : `$${monthlySavingAmount.toLocaleString()}`
                    : ''}
                </ResultsAccent>{' '}
                {
                  translationPlanning[language].TARGET_SAVING
                    .RESULTS_MONTH_SUFFIX
                }{' '}
                <ResultsAccent color={colors.blue}>
                  {yearsToTarget}
                </ResultsAccent>{' '}
                {
                  translationPlanning[language].TARGET_SAVING
                    .RESULTS_YEARS_SUFFIX
                }
              </ResultsTitle>
              {outcomeData ? (
                <StatsRow>
                  <StatBoxGroup>
                    <StatBoxBorder borderColor={colors.red} />
                    <StatBox>
                      <StatBoxLabel>
                        {
                          translationPlanning[language].TARGET_SAVING
                            .FUTURE_VALUE_LABEL
                        }
                      </StatBoxLabel>{' '}
                      <StatBoxValue>
                        {french
                          ? `${Math.ceil(
                              outcomeData.current.future_value
                            ).toLocaleString()} $`
                          : `$${Math.ceil(
                              outcomeData.current.future_value
                            ).toLocaleString()}`}
                        <RedDot />
                      </StatBoxValue>
                      <StatBoxSub>
                        {
                          translationPlanning[language].TARGET_SAVING
                            .IN_YEARS_PREFIX
                        }{' '}
                        {yearsToTarget}{' '}
                        {translationPlanning[language].TARGET_SAVING.YEARS_AT}{' '}
                        {inf}
                        {
                          translationPlanning[language].TARGET_SAVING
                            .INFLATION_SUFFIX
                        }
                      </StatBoxSub>
                    </StatBox>
                  </StatBoxGroup>

                  <StatBoxDivider>−</StatBoxDivider>

                  <StatBoxGroup>
                    <StatBoxBorder borderColor="#e6a817" />
                    <StatBox>
                      <StatBoxLabel>
                        {
                          translationPlanning[language].TARGET_SAVING
                            .TOTAL_CONTRIBUTIONS_LABEL
                        }
                      </StatBoxLabel>{' '}
                      <StatBoxValue>
                        {french
                          ? `${Math.ceil(
                              outcomeData.current.total_invested
                            ).toLocaleString()} $`
                          : `$${Math.ceil(
                              outcomeData.current.total_invested
                            ).toLocaleString()}`}
                      </StatBoxValue>
                      <StatBoxSub
                        style={{ whiteSpace: french ? 'normal' : 'nowrap' }}
                      >
                        {french
                          ? `${startingAmount.toLocaleString()} $`
                          : `$${startingAmount.toLocaleString()}`}{' '}
                        {translationPlanning[language].TARGET_SAVING.STARTING} +{' '}
                        {french
                          ? `${Math.ceil(
                              outcomeData.current.total_invested -
                                startingAmount
                            ).toLocaleString()} $`
                          : `$${Math.ceil(
                              outcomeData.current.total_invested -
                                startingAmount
                            ).toLocaleString()}`}{' '}
                        {translationPlanning[language].TARGET_SAVING.ADDITIONAL}
                      </StatBoxSub>{' '}
                    </StatBox>
                  </StatBoxGroup>

                  <StatBoxDivider>=</StatBoxDivider>

                  <StatBoxGroup>
                    <StatBoxBorder borderColor={colors.navyBlue} />
                    <StatBox borderColor={colors.navyBlue}>
                      <StatBoxLabel>
                        {
                          translationPlanning[language].TARGET_SAVING
                            .TOTAL_RETURNS_LABEL
                        }
                      </StatBoxLabel>{' '}
                      <StatBoxValue>
                        {french
                          ? `${Math.ceil(
                              outcomeData.current.total_returns
                            ).toLocaleString()} $`
                          : `$${Math.ceil(
                              outcomeData.current.total_returns
                            ).toLocaleString()}`}
                      </StatBoxValue>
                      <StatBoxSub>
                        <strong>
                          {Math.round(monthlySavingChosen * 100)}%
                        </strong>{' '}
                        {
                          translationPlanning[language].TARGET_SAVING
                            .TOTAL_RETURNS_SUB
                        }{' '}
                        {french
                          ? `${Math.ceil(
                              outcomeData.current.total_returns
                            ).toLocaleString()} $`
                          : `$${Math.ceil(
                              outcomeData.current.total_returns
                            ).toLocaleString()}`}
                      </StatBoxSub>{' '}
                    </StatBox>
                  </StatBoxGroup>
                </StatsRow>
              ) : null}
              <Header as="h3" style={{ margin: '2rem auto 0.25rem' }}>
                {translationPlanning[
                  language
                ].TARGET_SAVING.SIMULATED_OUTCOMES_TITLE.toUpperCase()}
              </Header>
              <ChartSubtitle>
                <ChartSubtitleAccent>
                  {Math.round(monthlySavingChosen * 100)}%
                </ChartSubtitleAccent>{' '}
                {translationPlanning[language].TARGET_SAVING.CHART_SUBTITLE_1}{' '}
                <RedDotInline />
              </ChartSubtitle>{' '}
              <div style={{ width: '80%', margin: 'auto' }}>
                <Line data={this.getChartData()} options={chartOptions} />
              </div>
            </ResultsContainer>
          ) : null}
        </div>
      </div>
    );
  }
}

SavingResult.propTypes = {
  result: PropTypes.object,
  inf: PropTypes.number,
  french: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  targetSavingInputs: PropTypes.object,
  startingAmount: PropTypes.number,
  yearsToTarget: PropTypes.number,
};

SavingResult.defaultProps = {
  result: null,
  inf: 2,
  french: false,
  targetSavingInputs: {},
  startingAmount: 0,
  yearsToTarget: 0,
};

export default connect((state) => ({
  result: state.Planning.savingResult,
  french: state.Storage.language === 'fr',
  targetSavingInputs: state.CacheInputs.planning.target_saving,
  startingAmount: state.CacheInputs.planning.target_saving.init || 0,
  yearsToTarget: state.CacheInputs.planning.target_saving.horz || 0,
}))(SavingResult);

const OptionCard = styled.div`
  border: 1px solid
    ${(props) => (props.selected ? props.borderColor : '#e5e7eb')};
  background: ${(props) => (props.selected ? '#f4f3ff' : '#fff')};
  border-radius: 8px;
  padding: 1.5rem 1.25rem 1.25rem;
  text-align: center;
  cursor: ${(props) => (props.able ? 'pointer' : 'default')};
  box-shadow: ${(props) =>
    props.selected ? `0 0 0 1px ${props.borderColor}` : 'none'};
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: ${(props) => (props.able ? props.borderColor : '#e5e7eb')};
  }
`;

const GaugeWrapper = styled.div`
  position: relative;
  width: 204px;
  max-width: 100%;
  margin: 0 auto;
`;

const CardDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 1.25rem 0 1rem;
`;

const CardLabel = styled.div`
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.6);
`;

const CardValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0.25rem 0 1rem;
`;

const CardValueUnit = styled.span`
  font-size: 1rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
  margin-left: 2px;
`;

const DetailsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.5rem 1.1rem;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.75);
  background: ${(props) => (props.selected ? '#e8ebff' : '#fff')};
  border: 1px solid
    ${(props) => (props.selected ? props.borderColor : '#d6d9de')};
  border-radius: 4px;
  cursor: pointer;

  i.icon {
    margin: 0 4px 0 0;
    color: rgba(0, 0, 0, 0.5);
  }
`;

const ResultsContainer = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  padding: 2rem 2rem 2rem;
  margin-top: 1.5rem;
`;

const ResultsTitle = styled.h2`
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 1.5rem;
  color: #1a1a1a;
`;

const ResultsAccent = styled.span`
  color: ${(props) => props.color || '#3b5998'};
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const StatBoxGroup = styled.div`
  display: flex;
  flex: 1;
  min-width: 183px;
  max-width: 263px;
`;

const StatBox = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: 0.75rem 1.25rem;
`;

const StatBoxLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.45);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
`;

const StatBoxValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0.25rem;
`;

const StatBoxSub = styled.div`
  font-size: 1rem;
  color: black;
`;

const StatBoxDivider = styled.div`
  font-size: 2rem;
  color: rgba(0, 0, 0, 0.3);
  padding: 0 0.75rem;
  flex-shrink: 0;
  align-self: center;
  margin-bottom: 1rem;
`;

const StatBoxBorder = styled.div`
  height: 90px;
  width: 0px;
  border-left: 3px solid ${(props) => props.borderColor || '#ccc'};
  align-self: center;
`;

const RedDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e53935;
  flex-shrink: 0;
`;

const RedDotInline = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e53935;
  vertical-align: middle;
  margin-left: 2px;
`;

const ChartSubtitle = styled.p`
  text-align: center;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.65);
  margin: 0 auto 1.5rem;
`;

const ChartSubtitleAccent = styled.span`
  font-weight: 700;
  color: ${colors.blue};
`;
